import Gtm from 'next-gtm';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import useSWR from 'swr';
import BingoLobbyModal from '../components/bingolobbymodal/bingolobbymodal';
import BingoThumbnail from '../components/bingothumbnail/bingothumbnail';
import Loader from '../components/loader/loader';
import LobbyButton from '../components/lobbybuttons/lobbybutton';
import SkillBingoThumbnail from '../components/skillbingothumbnail/skillbingothumbnail';
import { useIframeResize } from '../hooks/use-iframe-resize';
import { useRooms } from '../hooks/use-rooms';
import { useUpdateRooms } from '../hooks/use-update-rooms';
import CONFIG from '../utils/config';
import { getFormattedCurrency } from '../utils/currency';

import '@parlaygames/bingomarketingmodal/es/bingomarketingmodal.css';
import { apiClient, apiFetcher } from '../utils/axios-client';

import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { useTheme } from 'styled-components';
import VideoBingoThumbnail from '../components/VideoBingoThumbnail';

const mobile = '(min-width: 1px) and (max-width: 576px)';

const LobbyBody = styled.div`
    min-height: 700px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    background: transparent;
`;

const ScheduleDiv = styled.div`
    position: relative;
    padding: 63px 0 20px 0;
    display: grid;
    grid-template-columns: ${(props) =>
        props.itemsLength < 4
            ? `repeat(${props.itemsLength}, 0.01fr)`
            : 'repeat(4, 0.01fr)'};
    grid-column-gap: 2vw;
    grid-row-gap: 25px;
    justify-content: center;
    justify-items: center;

    @media (min-width: 576px) and (max-width: 1100px) {
        grid-template-columns: ${(props) =>
            props.itemsLength < 2
                ? `repeat(${props.itemsLength}, 0.1fr)`
                : 'repeat(2, 0.1fr)'};
        grid-column-gap: 1vw;
        grid-row-gap: 25px;
    }

    @media ${mobile} {
        grid-template-columns: ${(props) =>
            props.itemsLength < 2
                ? `repeat(${props.itemsLength}, 0.4fr)`
                : 'repeat(2, 0.4fr)'};
        grid-column-gap: 0.5vw;
        padding: 14vw 0 5vw 0;
    }
`;

const EmptyText = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 30px;
    color: white;
    padding: 40px 0px 40px 0px;
    text-align: center;
    caret-color: transparent;

    p {
        background: #333;
        padding: 1rem;
        border-radius: 1rem;
    }
`;

const ButtonsDiv = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    border-top: solid 1.5px #414172;
    border-bottom: ${(props) =>
        props.skillBingo.length ? `solid 1.5px #414172` : null};

    flex-wrap: wrap;

    @media ${mobile} {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
    }
`;

const TitleText = styled.h1`
    position: absolute;
    left: 15px;
    top: 18px;
    font-family: Roboto;
    font-size: 20px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.8;
    letter-spacing: normal;
    color: #19b1f9;
    margin: 0;
`;

const TitleTextSkill = styled(TitleText)`
    top: 30px;
    left: 50px;
    @media ${mobile} {
        left: 2vw;
    }
`;

const VideoBingoContainer = styled.div`
    border-bottom: solid 1.5px #414172;
    width: 100%;
    display: flex;
    justify-content: center;
`;

const VideoBingoDiv = styled.div`
    position: relative;
    padding: 60px 0px 0 0px;
    margin: 0 20px 0 20px;
    max-width: 1300px;

    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
`;

const SkillBingoDiv = styled.div`
    position: relative;
    padding: 60px 0px 0 0px;
    margin: 0 20px 0 20px;
    max-width: 1300px;

    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
`;

const SkillBingoTextDivContainer = styled.div`
    display: flex;
    align-items: center;
    padding-bottom: 80px;
`;

const SkillBingoTextDiv = styled.img`
    width: 339px;
    height: 143px;
    padding-top: 30px;

    @media ${mobile} {
        padding-top: 0;
        transform: scale(0.8);
    }
`;

const BingoMarketingModal = dynamic(
    () => import(`@parlaygames/bingomarketingmodal`),
    {
        ssr: false,
    }
);

const BingoIframe = (props) => {
    const { t } = useTranslation('common');
    const router = useRouter();
    const { token, userId, parlaySite, lang, banner } = router.query;
    const [sessionId, setSessionId] = useState(token);
    const [isOpen, setOpen] = useState(false);
    const [aliasChange, setAliasChange] = useState(false);
    const [skillBingoRooms, setSkillBingoRooms] = useState([]);
    const [videoBingoRooms, setVideoBingoRooms] = useState([]);
    const [accountStatus, setAccountStatus] = useState({
        cash: '0',
        bonus: '0',
        balance: '0',
    });
    const theme = useTheme();
    const isVideoBingoHidden = theme.videoBingo?.hidden === true;
    const isSkillBingoHidden = theme.skillBingo?.hidden === true;
    const marketingModalImageMap = {
        featuredEvent: theme.marketingModalImageMap?.featuredEvent,
        tournaments: theme.marketingModalImageMap?.tournaments,
        dailyEvent: theme.marketingModalImageMap?.dailyEvent,
    };

    const [config, setConfig] = useState(null);

    const {
        data: scheduledBingoRooms,
        mutate: scheduledBingoRoomsMutate,
        isValidating: isScheduledBingoRoomsLoading,
    } = useSWR(`/get-bingo-rooms?lang=${lang}`, apiFetcher);
    const { data: currencies } = useSWR('/currencies', apiFetcher);
    const { data: playerBalance, mutate: mutatePlayerBalance } = useSWR(
        sessionId ? '/player-balance' : null,
        apiFetcher,
        {
            revalidateOnFocus: false,
        }
    );

    const bingoRooms = useRooms(scheduledBingoRooms);

    const { data: sessionData, mutate: sessionMutate } = useSWR(
        '/checksession',
        apiFetcher
    );

    useUpdateRooms(scheduledBingoRoomsMutate, scheduledBingoRooms);

    useIframeResize();

    useEffect(() => {
        const config = {
            ...props.config,
            jSessionToken: sessionId,
        };

        if (window.location.protocol === 'https:') {
            config.contentfulHost = window.location.host;
            config.contentfulBasePath = '/api/contentful';
        }

        setConfig(config);
    }, [props.config, sessionId]);

    useEffect(() => {
        if (sessionData) {
            if (!sessionData.isValid) {
                setSessionId(undefined);
                return;
            }

            if (sessionData.isValid === 'no account') {
                setSessionId(undefined);
                return;
            }

            setSessionId(token);
        }
    }, [sessionData]);

    useEffect(() => {
        // Notify parent window that the bingo lobby has loaded
        if (scheduledBingoRooms && window.parent !== window) {
            window.parent.postMessage(
                { type: 'parlay:bingo-lobby-loaded' },
                '*'
            );
        }
    }, [scheduledBingoRooms]);

    useEffect(() => {
        async function process() {
            const aliasData = await apiClient.get('/site/v2/player/alias');
            setAliasChange(aliasData.data.change);
            sessionMutate();
        }

        if (sessionId) {
            process();
        }
    }, [sessionId]);

    useEffect(() => {
        async function getSkillBingoGames() {
            try {
                const { data } = await apiClient.get('/site/v2/casino/games', {
                    params: {
                        gameType: 'SB',
                        lang,
                    },
                });

                const seen = new Set();
                const rooms = data
                    .filter((item) => item.tags.includes('Lobby'))
                    .map((item) => ({
                        gameId: item.gameId,
                        style: item.style,
                        thumbnail: item.thumbnail,
                    }))
                    .filter((item) => {
                        if (seen.has(item.gameId)) return false;
                        seen.add(item.gameId);
                        return true;
                    });

                setSkillBingoRooms(rooms);
            } catch (error) {}
        }

        getSkillBingoGames();
    }, [parlaySite]);

    useEffect(() => {
        async function getVideoBingoGames() {
            try {
                const { data } = await apiClient.get('/site/v2/casino/games', {
                    params: {
                        gameType: 'VB',
                        lang,
                    },
                });

                const seen = new Set();
                const rooms = data
                    .filter((item) => item.tags.includes('Lobby'))
                    .map((item) => ({
                        gameId: item.gameId,
                        style: item.style,
                        thumbnail: item.thumbnail,
                    }))
                    .filter((item) => {
                        if (seen.has(item.gameId)) return false;
                        seen.add(item.gameId);
                        return true;
                    });

                setVideoBingoRooms(rooms);
            } catch (error) {}
        }

        getVideoBingoGames();
    }, [parlaySite]);

    const refreshBalance = async () => {
        if (!sessionId) {
            setAccountStatus({
                cash: '0',
                bonus: '0',
                balance: '0',
            });
            return;
        }

        await mutatePlayerBalance();
    };

    useEffect(() => {
        if (!sessionId) {
            setAccountStatus({
                cash: '0',
                bonus: '0',
                balance: '0',
            });
            return;
        }

        if (!playerBalance) {
            return;
        }

        const { cash, bonus, total, currency } = playerBalance;
        const currencyMeta = currencies?.[currency];

        setAccountStatus({
            cash: getFormattedCurrency(Number(cash), currency, currencyMeta),
            bonus: getFormattedCurrency(Number(bonus), currency, currencyMeta),
            balance: getFormattedCurrency(
                Number(total),
                currency,
                currencyMeta
            ),
        });
    }, [currencies, playerBalance, sessionId]);

    const setModalOpen = (path) => {
        setConfig((config) => ({
            ...config,
            isOpen: true,
            defaultTab: path,
        }));
    };

    const closeModal = () => {
        setConfig((config) => ({
            ...config,
            isOpen: false,
            defaultTab: 'featured',
        }));
    };

    /**
     * @param {string|number} roomId
     */
    const onPreBuy = (roomId) => {
        setConfig((config) => ({
            ...config,
            isOpen: true,
            roomId,
            defaultTab: 'pre-purchase',
        }));
    };

    const toggleChangeAliasModal = () => {
        setAliasChange(false);
    };

    const renderRooms = () => {
        if (!bingoRooms.length) {
            return (
                <EmptyText>
                    {isScheduledBingoRoomsLoading ? (
                        <>
                            {' '}
                            {t('lobby.checkingRooms')} <Loader />{' '}
                        </>
                    ) : (
                        <p>{t('lobby.noRooms')}</p>
                    )}
                </EmptyText>
            );
        }
        return (
            <ScheduleDiv itemsLength={bingoRooms.length}>
                <TitleText>{t('lobby.classicBingo')}</TitleText>
                {bingoRooms.map((bingoData) => (
                    <BingoThumbnail
                        key={bingoData.id}
                        bingoData={bingoData}
                        onPreBuy={onPreBuy}
                        sessionToken={sessionId}
                        userId={userId}
                        aliasChange={aliasChange}
                        lang={lang}
                    />
                ))}
            </ScheduleDiv>
        );
    };

    const hasContentfulConfig =
        !!config?.contentfulAccessToken &&
        !!config?.contentfulSpace &&
        !!config?.contentfulEnvironment;

    return (
        <>
            <Head>
                <title>{t('lobby.title')}</title>
                <meta name="description" content={t('lobby.description')} />
                {process.env.GOOGLETAGID ? (
                    <Gtm id={process.env.GOOGLETAGID} />
                ) : null}
            </Head>
            <LobbyBody parlaySite={parlaySite}>
                {renderRooms()}
                {hasContentfulConfig && (
                    <BingoMarketingModal
                        {...config}
                        cash={accountStatus.cash}
                        bonus={accountStatus.bonus}
                        balance={accountStatus.balance}
                        refreshBalance={refreshBalance}
                        onClose={closeModal}
                        locales={[lang]}
                        theme={theme.marketingModalTheme}
                    />
                )}

                {/* isOpen={isOpen.open} */}
                {/* defaultTab={isOpen.path} */}

                {banner !== 'false' && hasContentfulConfig && (
                    <ButtonsDiv skillBingo={skillBingoRooms}>
                        <LobbyButton
                            backgroundSrc={marketingModalImageMap.featuredEvent}
                            path={'featured'}
                            setModalOpen={setModalOpen}
                        />
                        <LobbyButton
                            backgroundSrc={marketingModalImageMap.tournaments}
                            path={'tournaments-events'}
                            setModalOpen={setModalOpen}
                        />
                        <LobbyButton
                            backgroundSrc={marketingModalImageMap.dailyEvent}
                            path={'daily-events'}
                            setModalOpen={setModalOpen}
                        />
                    </ButtonsDiv>
                )}
                {!isVideoBingoHidden && videoBingoRooms.length ? (
                    <VideoBingoContainer>
                        <VideoBingoDiv itemsLength={videoBingoRooms.length}>
                            <TitleTextSkill>
                                {t('lobby.videoBingo')}
                            </TitleTextSkill>
                            {videoBingoRooms.map((room, index) => (
                                <VideoBingoThumbnail
                                    key={`${room.gameId}-${index}`}
                                    thumbnail={room.thumbnail}
                                    sessionId={sessionId}
                                    userId={userId}
                                    parlaySite={parlaySite}
                                    gameId={room.gameId}
                                    lang={lang}
                                />
                            ))}
                        </VideoBingoDiv>
                    </VideoBingoContainer>
                ) : null}
                {!isSkillBingoHidden && skillBingoRooms.length ? (
                    <SkillBingoDiv itemsLength={skillBingoRooms.length}>
                        <TitleTextSkill>{t('lobby.skillBingo')}</TitleTextSkill>
                        {skillBingoRooms.map((room) => (
                            <SkillBingoThumbnail
                                key={room.gameId}
                                thumbnail={room.thumbnail}
                                sessionId={sessionId}
                                userId={userId}
                                parlaySite={parlaySite}
                                gameId={room.gameId}
                                lang={lang}
                            />
                        ))}
                    </SkillBingoDiv>
                ) : null}
            </LobbyBody>

            <BingoLobbyModal
                userId={userId}
                toggleChangeAliasModal={toggleChangeAliasModal}
            />
        </>
    );
};

export const getServerSideProps = async ({ query, locale }) => {
    let contentful = {};
    let siteId = '';
    let theme = '';

    try {
        const apiData = JSON.parse(CONFIG.PARLAY_SITE);
        siteId = apiData[query.parlaySite]?.siteId || '';
        theme = apiData[query.parlaySite]?.theme || '';
    } catch (error) {
        console.error(error);
    }

    try {
        const envContentful = JSON.parse(CONFIG.CONTENTFUL);
        contentful = envContentful[siteId] || {};
    } catch (error) {
        console.error(error);
    }

    return {
        props: {
            theme,
            config: {
                siteId,
                contentfulSpace: contentful.CONTENTFUL_MARKETING_SPACE_ID || '',
                contentfulEnvironment:
                    contentful.CONTENTFUL_MARKETING_ENV_ID || '',
                contentfulAccessToken:
                    contentful.CONTENTFUL_MARKETING_TOKEN || '',
            },
            ...(await serverSideTranslations(query.lang || locale || 'en', [
                'common',
                'bingo',
            ])),
        },
    };
};

export default BingoIframe;
